import React from 'react'
import { Trans } from 'react-i18next'
import moment from 'moment'

import { translateEaseOfRank } from 'helpers/keyword'
import {
  renderRankFormatter,
  renderInListingFormatter
} from 'helpers/rank_tracker/formatters'
import { bidFormatter, volumeFormatter } from 'helpers/keyword_engine'

import { RANK_TRACKER_KEYWORD_DETAILS_DATA_ATTRIBUTE_IDS as DataIds } from 'constants/data_attributes/rank_tracker/keyword_details'
import { KEYWORD_RANK_TRACKER_TOOLTIPS } from 'constants/rank_tracker/tooltips'

export const MY_PRODUCTS_SOURCE = 'my_products'
// const TRACKED_PRODUCTS_SOURCE = 'tracked_products'
export const NEW_ENTRY_SOURCE = 'new_entry'

// keywords sources
export const COMPETITORS_SOURCE = 'competitors'
export const KEYWORD_LISTS_SOURCE = 'keyword_lists'
export const MAX_KEYWORD_BANK_LIMIT_PER_PRODUCT = 500
// const TRACKED_PRODUCTS_SOURCE = 'tracked_products'

export const KEYWORD_RANK_TRACKER_KEYWORD_COLUMNS = [
  {
    index: 0,
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_KEYWORD_COLUMNS.name">
        Keyword
      </Trans>
    ),
    sortable: true,
    accessor: 'name',
    dataId: DataIds.KeywordTable.SortByKeyword
  },
  {
    index: 1,
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_KEYWORD_COLUMNS.exactMatchSearchVolume">
        Search Volume (Exact)
      </Trans>
    ),
    sortable: true,
    accessor: 'exactSearchVolume',
    formatter: val => volumeFormatter(val),
    dataId: DataIds.KeywordTable.SortBySearchVolume
  },
  {
    index: 2,
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_KEYWORD_COLUMNS.organicRank">
        Organic Rank / Page
      </Trans>
    ),
    tooltipContent: KEYWORD_RANK_TRACKER_TOOLTIPS.organicRank,
    sortable: true,
    accessor: 'displayedOrganicRank',
    formatter: (val, item) => {
      const rank = item?.currentRankData?.organic?.organicRank
      const page = item?.currentRankData?.organic?.page
      const oldRank = item?.previousRankData?.organic?.organicRank
      const oldPage = item?.previousRankData?.organic?.page
      const updatedAt = item?.updatedAt
      return renderRankFormatter(rank, page, oldRank, oldPage, updatedAt)
    },
    dataId: DataIds.KeywordTable.SortByOrganicRank
  },
  {
    index: 3,
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_KEYWORD_COLUMNS.sponsoredRank">
        Sponsored Rank
      </Trans>
    ),
    tooltipContent: KEYWORD_RANK_TRACKER_TOOLTIPS.sponsoredRank,
    sortable: true,
    accessor: 'displayedSponsoredRank',
    formatter: (val, item) => {
      const rank = item?.currentRankData?.sponsored?.sponsoredRank
      const oldRank = item?.previousRankData?.sponsored?.sponsoredRank
      const updatedAt = item?.updatedAt
      return renderRankFormatter(rank, null, oldRank, null, updatedAt)
    },
    dataId: DataIds.KeywordTable.SortBySponsoredRank
  },
  {
    index: 3,
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_KEYWORD_COLUMNS.inListing">
        In Listing
      </Trans>
    ),
    tooltipContent: KEYWORD_RANK_TRACKER_TOOLTIPS.inListing,
    sortable: true,
    accessor: 'inListing',
    formatter: val => renderInListingFormatter(val),
    dataId: DataIds.KeywordTable.SortByInListing
  },
  {
    index: 4,
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_KEYWORD_COLUMNS.easeOfRankingScore">
        Ease to Rank
      </Trans>
    ),
    tooltipContent: KEYWORD_RANK_TRACKER_TOOLTIPS.easeOfRankingScore,
    sortable: true,
    accessor: 'easeOfRankingScore',
    formatter: val => translateEaseOfRank(val),
    dataId: DataIds.KeywordTable.SortByEaseToRank
  },
  {
    index: 5,
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_KEYWORD_COLUMNS.ppcBidExact">
        PPC Bid (Exact)
      </Trans>
    ),
    tooltipContent: KEYWORD_RANK_TRACKER_TOOLTIPS.ppcBidExact,
    sortable: true,
    accessor: 'exactSuggestedBidMedian',
    formatter: (val, item) =>
      bidFormatter(val, item, 'exactSuggestedBidMedian'),
    dataId: DataIds.KeywordTable.SortByPPCBid
  },
  {
    index: 6,
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_KEYWORD_COLUMNS.lastCheckedDate">
        Last Checked
      </Trans>
    ),
    accessor: 'lastUpdated'
  },
  {
    name: '',
    accessor: 'actions'
  }
]

export const KEYWORD_FILTERS = [
  {
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_KEYWORD_FILTERS.selectAll">
        Select All
      </Trans>
    ),
    value: 'selectAll'
  },
  {
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_KEYWORD_FILTERS.trackingNotIndexed">
        Tracking, Not Indexed
      </Trans>
    ),
    value: 'trackingNotIndexed'
  },
  {
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_KEYWORD_FILTERS.indexedTrendingUp">
        Indexed, Trending Up
      </Trans>
    ),
    value: 'indexedTrendingUp'
  },
  {
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_KEYWORD_FILTERS.indexedTrendingDown">
        Indexed, Trending Down
      </Trans>
    ),
    value: 'indexedTrendingDown'
  },
  {
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_KEYWORD_FILTERS.easeToRankIndexed">
        Easy to Rank, Indexed
      </Trans>
    ),
    value: 'easeToRankIndexed'
  },
  {
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_KEYWORD_FILTERS.easyToRankNotIndexed">
        Easy to Rank, Not Indexed
      </Trans>
    ),
    value: 'easyToRankNotIndexed'
  }
]

export const KEYWORD_FILTERS_OBJ = {
  selectAll: KEYWORD_FILTERS[0].value,
  trackingNotIndexed: KEYWORD_FILTERS[1].value,
  indexedTrendingUp: KEYWORD_FILTERS[2].value,
  indexedTrendingDown: KEYWORD_FILTERS[3].value,
  easyToRankIndexed: KEYWORD_FILTERS[4].value,
  easyToRankNotIndexed: KEYWORD_FILTERS[5].value
}

export const ANNOTATION_COLUMNS = [
  {
    index: 0,
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_ANNOTAION_COLUMNS.annotationDate">
        Annotation Date
      </Trans>
    ),
    size: 'small',
    accessor: 'annotation_date',
    formatter: date => moment(date).format('MMM DD, YYYY')
  },
  {
    index: 1,
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_ANNOTAION_COLUMNS.annotation">
        Annotation
      </Trans>
    ),
    size: 'xx-large',
    accessor: 'content'
  },
  {
    index: 2,
    name: (
      <Trans i18nKey="constants:KEYWORD_RANK_TRACKER_ANNOTAION_COLUMNS.author">
        Author
      </Trans>
    ),
    size: 'small',
    accessor: 'author_name'
  },
  {
    name: '',
    accessor: 'actions',
    sortable: false,
    size: 'small'
  }
]
